import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { filterDataApi } from '@/api/services/filterData';
import { criticalModificationApi } from '@/api/services/criticalModification';
import { useEnumOptions } from '@/features/common/enums/useEnumOptions';
import type {
  CriticalModificationDto,
  CriticalModificationFilterInput,
} from '@/api/types/criticalModification';

function pad(n: number) {
  return String(n).padStart(2, '0');
}
function fmtDt(d: Date, end = false) {
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return `${date}T${end ? '23:59:59' : '00:00:00'}`;
}
function todayStart() {
  return fmtDt(new Date());
}
function todayEnd() {
  return fmtDt(new Date(), true);
}
const fmtTs = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

interface CmFilters {
  startDate: string;
  endDate: string;
  deal: number | null;
  login: number | null;
  connectors: number[];
  actions: number[];
  flags: number[];
  type: string;
}

function defaults(): CmFilters {
  return { startDate: todayStart(), endDate: todayEnd(), deal: null, login: null, connectors: [], actions: [], flags: [], type: '' };
}

function toInput(f: CmFilters): CriticalModificationFilterInput {
  return {
    startDate: f.startDate,
    endDate: f.endDate,
    deal: f.deal ?? null,
    login: f.login ?? null,
    connectors: f.connectors,
    actions: f.actions,
    flags: f.flags,
    type: f.type === '' ? null : Number(f.type),
  };
}

export function CriticalModificationPage() {
  const [draft, setDraft] = useState<CmFilters>(defaults);
  const [applied, setApplied] = useState<CmFilters>(draft);

  const connectors = useQuery({ queryKey: ['cm-filter', 'nonTerminalConnectors'], queryFn: filterDataApi.getNonTerminalConnectors, staleTime: 5 * 60_000 });

  const { options: enums } = useEnumOptions(['ModificationAction', 'ModificationFlag', 'DealCategory']);
  const modificationActions = enums.ModificationAction ?? [];
  // Backend list includes FLAG_ALL=255 — exclude it to match legacy.
  const modificationFlags = (enums.ModificationFlag ?? []).filter((o) => o.value !== 255);
  const dealCategories = enums.DealCategory ?? [];

  const appliedInput = useMemo(() => toInput(applied), [applied]);
  const table = useServerDataTable<CriticalModificationDto, CriticalModificationFilterInput>({
    queryKey: ['critical-modification', 'rows'],
    fetcher: (req) => criticalModificationApi.getList(req),
    pageSize: 5,
    extraParams: appliedInput,
  });

  const fetch = () => {
    table.setPageIndex(0);
    setApplied({ ...draft });
  };
  const clear = () => {
    const d = defaults();
    setDraft(d);
    table.setPageIndex(0);
    setApplied(d);
  };

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Time Range</label>
          <DateRangePicker mode="range" enableTime timeSeconds maxDate={todayEnd()} value={{ start: draft.startDate, end: draft.endDate }} onChange={(v) => setDraft((f) => ({ ...f, startDate: v.start, endDate: v.end }))} />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <div key="deal">
      <label className="form-label">Ticket</label>
      <input className="form-control" inputMode="numeric" value={draft.deal ?? ''} onChange={(e) => setDraft((f) => ({ ...f, deal: e.target.value ? Number(e.target.value) : null }))} />
    </div>,
    <div key="login">
      <label className="form-label">Login</label>
      <input className="form-control" inputMode="numeric" value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} />
    </div>,
    <div key="connectors">
      <label className="form-label">Connector</label>
      <Select2Multi placeholder="All" value={draft.connectors.map(String)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, connectors: v.map(Number) }))} />
    </div>,
    <div key="actions">
      <label className="form-label">Action</label>
      <Select2Multi placeholder="All" value={draft.actions.map(String)} options={modificationActions} onChange={(v) => setDraft((f) => ({ ...f, actions: v.map(Number) }))} />
    </div>,
    <div key="flags">
      <label className="form-label">Modification Flag</label>
      <Select2Multi placeholder="All" value={draft.flags.map(String)} options={modificationFlags} onChange={(v) => setDraft((f) => ({ ...f, flags: v.map(Number) }))} />
    </div>,
    <Select2 key="type" label="Type" allowClear placeholder="All" value={draft.type} options={dealCategories} onChange={(v) => setDraft((f) => ({ ...f, type: v ?? '' }))} />,
  ];

  return (
    <div className="critical-modification-page">
      <PageHeader title="Critical Modification" breadcrumb={['Home', 'Critical Modification']} />

      <FilterBar
        fields={fields}
        actions={
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={fetch} disabled={table.isFetching}>
              {table.isFetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
            </button>
            <button type="button" className="btn btn-link btn-sm" onClick={clear}>Clear All Filters</button>
          </>
        }
      />

      <DataGrid
        columns={[
          { key: 'deal', title: 'Ticket', className: 'text-start', value: (r) => r.deal },
          { key: 'platform', title: 'Connector', className: 'text-start', value: (r) => r.platform },
          { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login },
          { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
          { key: 'fieldChanged', title: 'Field Changed', className: 'text-start', value: (r) => r.fieldChanged },
          { key: 'action', title: 'Action', className: 'text-start', value: (r) => r.action },
          { key: 'originalValue', title: 'Original Value', className: 'text-start', value: (r) => r.originalValue },
          { key: 'latestValue', title: 'Latest Value', className: 'text-start', value: (r) => r.latestValue },
          { key: 'type', title: 'Type', className: 'text-start', value: (r) => r.type },
          { key: 'modificationFlag', title: 'Modification Flag', className: 'text-start', value: (r) => r.modificationFlag },
          { key: 'modificationDate', title: 'Modification Date', className: 'text-start', value: (r) => r.modificationDate, cell: (r) => fmtTs(r.modificationDate) },
        ] as DataGridColumn<CriticalModificationDto>[]}
        data={table.rows}
        rowKey={(r) => `${r.deal}-${r.modificationDate}-${r.fieldChanged}`}
        card
        includeExcel
        excelFileName="CriticalModification"
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />
    </div>
  );
}
